import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  HomeIcon,
  SettingsIcon,
  Language,
  NavCalendar,
  TasksIcon,
  Messages,
} from "../../shared/icons";
import WalletOutlined from "components/shared/svg-action-icons/WalletOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBullhorn } from "@fortawesome/free-solid-svg-icons";
import { LANGUAGE } from "services/general";
import { HAS_PERMISSION } from "services/user";
import { WALLET_INDEX } from "permissions";

export default function useMoreToolsMenu({ changeLng, handleSettings }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef(null);

  // Close dropdown on click outside when on tablet/desktop (>= 640px)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        window.innerWidth >= 640 &&
        moreMenuRef.current &&
        !moreMenuRef.current.contains(event.target)
      ) {
        setMoreMenuOpen(false);
      }
    };

    if (moreMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [moreMenuOpen]);

  const closeMenu = () => setMoreMenuOpen(false);

  const moreMenuItems = [
    {
      id: "more-home",
      icon: <HomeIcon isDark={false} />,
      label: t("pages.home"),
      onClick: () => {
        navigate(`/${LANGUAGE}`);
        closeMenu();
      },
    },
    ...(HAS_PERMISSION(WALLET_INDEX)
      ? [
          {
            id: "more-wallet",
            icon: <WalletOutlined width="20" height="20" />,
            label: t("wallet.pageTitle"),
            onClick: () => {
              navigate(`/${LANGUAGE}/wallet`);
              closeMenu();
            },
          },
        ]
      : []),
    {
      id: "more-settings",
      icon: <SettingsIcon isDark={false} />,
      label: t("pages.settings"),
      onClick: () => {
        handleSettings?.();
        closeMenu();
      },
    },
    {
      id: "more-language",
      icon: <Language isDark={false} />,
      label: t("change_language"),
      onClick: () => {
        changeLng?.();
        closeMenu();
      },
    },
    {
      id: "more-notes",
      icon: <NavCalendar isDark={false} />,
      label: t("pages.notes"),
      onClick: () => {
        navigate(`/${LANGUAGE}/notes`);
        closeMenu();
      },
    },
    {
      id: "more-tasks",
      icon: <TasksIcon isDark={false} />,
      label: t("pages.tasks"),
      onClick: () => {
        navigate(`/${LANGUAGE}/tasks`);
        closeMenu();
      },
    },
    {
      id: "more-support",
      icon: <Messages isDark={false} />,
      label: t("messages.title"),
      onClick: () => {
        navigate(`/${LANGUAGE}/support-center`);
        closeMenu();
      },
    },
    {
      id: "more-announcements",
      icon: <FontAwesomeIcon icon={faBullhorn} className="text-[18px]" />,
      label: t("announcements.title"),
      onClick: () => {
        navigate(`/${LANGUAGE}/announcements`);
        closeMenu();
      },
    },
  ];

  return { moreMenuOpen, setMoreMenuOpen, moreMenuRef, moreMenuItems };
}
